import React, { useMemo, useEffect, useRef, useState } from "react";

type SeriesItem = { name: string; data: number[] };
type TooltipState = {
  x: number;
  y: number;
  seriesName: string;
  catLabel: string;
  displayValue: string;
} | null;

const BAR_COLOR = "#74B4F8";
const PADDING = { top: 30, right: 10, bottom: 36, left: 56 };

function niceMax(raw: number) {
  if (raw === 0) return 100;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  return Math.ceil(raw / mag) * mag;
}
function niceTicks(max: number, count = 5) {
  return Array.from({ length: count + 1 }, (_, i) =>
    Math.round((max / count) * i),
  );
}
function roundedTopRect(x: number, y: number, w: number, h: number, r: number) {
  if (h <= 0) return "";
  r = Math.min(r, h, w / 2);
  return [
    `M${x + r} ${y}`,
    `H${x + w - r}`,
    `Q${x + w} ${y} ${x + w} ${y + r}`,
    `V${y + h}`,
    `H${x}`,
    `V${y + r}`,
    `Q${x} ${y} ${x + r} ${y}Z`,
  ].join(" ");
}

const BarChart = ({
  className,
  style,
  data,
  categories,
  height = 350,
  labelHoverFormatter,
  title,
  subtitle,
  withAnimation = true,
}: {
  className?: string;
  style?: React.CSSProperties;
  data?: SeriesItem[] | number[];
  categories?: string[];
  height?: number;
  labelHoverFormatter?: (value: string) => string;
  title?: string;
  subtitle?: string;
  withAnimation?: boolean;
}) => {
  return (
    <div
      className={`rounded-card bg-surface shadow-card ${className ?? ""}`}
      style={style}>
      {(title || subtitle) && (
        <div className="px-5 pt-5">
          <h3 className="flex flex-col items-start">
            <span className="mb-1 text-xl font-bold text-gray-900">
              {title}
            </span>
            <span className="text-sm font-semibold text-muted">{subtitle}</span>
          </h3>
        </div>
      )}
      <div className="px-4 pb-4 pt-2">
        <SVGBarChart
          data={data}
          categories={categories}
          height={height}
          labelHoverFormatter={labelHoverFormatter}
          withAnimation={withAnimation}
        />
      </div>
    </div>
  );
};

export default BarChart;

function SVGBarChart({
  data,
  categories,
  height,
  labelHoverFormatter,
  withAnimation,
}: {
  data?: SeriesItem[] | number[];
  categories?: string[];
  height: number;
  labelHoverFormatter?: (value: string) => string;
  withAnimation: boolean;
}) {
  const [tooltip, setTooltip] = useState<TooltipState>(null);
  const [containerWidth, setContainerWidth] = useState(700);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver(([e]) =>
      setContainerWidth(e.contentRect.width || 700),
    );
    ro.observe(containerRef.current);
    setContainerWidth(containerRef.current.offsetWidth || 700);
    return () => ro.disconnect();
  }, []);

  const series: SeriesItem[] = useMemo(() => {
    if (!data || data.length === 0) return [];
    if (typeof data[0] === "number")
      return [{ name: "Value", data: data as number[] }];
    return data as SeriesItem[];
  }, [data]);

  const cats = categories ?? ["Feb", "Mar", "Apr", "May", "Jun", "Jul"];
  const numGroups = cats.length;
  const numSeries = series.length;

  const allValues = series.flatMap((s) => s.data);
  const yMax = niceMax(allValues.length > 0 ? Math.max(...allValues) : 100);
  const ticks = niceTicks(yMax, 5);

  const W = containerWidth;
  const innerW = W - PADDING.left - PADDING.right;
  const innerH = height - PADDING.top - PADDING.bottom;
  const groupWidth = innerW / numGroups;
  const barWidth = Math.max(
    4,
    Math.min(28, (groupWidth * 0.55) / Math.max(numSeries, 1)),
  );
  const groupPad = (groupWidth - barWidth * numSeries) / 2;
  const baselineY = PADDING.top + innerH;

  const TOOLTIP_W = 160,
    TOOLTIP_H = 52;

  return (
    <div ref={containerRef} style={{ position: "relative", width: "100%" }}>
      {withAnimation && (
        <style>{`
          @keyframes _bar_grow {
            from { transform: scaleY(0); }
            to   { transform: scaleY(1); }
          }
          .bar-animated {
            transform-origin: bottom;
            animation: _bar_grow 0.5s cubic-bezier(0.34, 1.2, 0.64, 1) both;
          }
        `}</style>
      )}
      <svg
        width={W}
        height={height}
        style={{
          display: "block",
          overflow: "visible",
          fontFamily: "inherit",
        }}>
        {ticks.map((tick) => {
          const y = PADDING.top + innerH - (tick / yMax) * innerH;
          return (
            <g key={tick}>
              <line
                x1={PADDING.left}
                x2={PADDING.left + innerW}
                y1={y}
                y2={y}
                stroke="#EAEAEF"
                strokeWidth={1}
                strokeDasharray="5 4"
              />
              <text
                x={PADDING.left - 10}
                y={y + 4}
                textAnchor="end"
                fontSize={11}
                fill="#B0B0C0">
                {tick.toLocaleString()}
              </text>
            </g>
          );
        })}

        {series.map((s, si) =>
          s.data.map((val, gi) => {
            const barH = (val / yMax) * innerH;
            const x = PADDING.left + gi * groupWidth + groupPad + si * barWidth;
            const y = baselineY - barH;
            const rx = Math.min(4, barWidth / 2);

            const displayValue = labelHoverFormatter
              ? labelHoverFormatter(val.toString())
              : val.toLocaleString();
            const rawTX = x + barWidth / 2 - TOOLTIP_W / 2;
            const tx = Math.max(
              PADDING.left,
              Math.min(rawTX, W - PADDING.right - TOOLTIP_W),
            );
            const ty = Math.max(4, y - TOOLTIP_H - 10);
            const delay = `${gi * 35}ms`;

            return (
              <path
                key={`${si}-${gi}`}
                d={roundedTopRect(x, y, barWidth, barH, rx)}
                fill={BAR_COLOR}
                className={withAnimation ? "bar-animated" : undefined}
                style={{
                  cursor: "pointer",
                  transition: "opacity 0.15s",
                  ...(withAnimation
                    ? {
                        transformOrigin: `${x + barWidth / 2}px ${baselineY}px`,
                        animationDelay: delay,
                      }
                    : {}),
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as SVGPathElement).style.opacity = "0.7";
                  setTooltip({
                    x: tx,
                    y: ty,
                    seriesName: s.name === "Value" ? "" : s.name,
                    catLabel: cats[gi] ?? "",
                    displayValue,
                  });
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as SVGPathElement).style.opacity = "1";
                  setTooltip(null);
                }}
              />
            );
          }),
        )}

        {/* label start */}
        {cats.map((cat, gi) => (
          <text
            key={`cat-${gi}`}
            x={PADDING.left + gi * groupWidth + groupWidth / 2}
            y={PADDING.top + innerH + 18}
            textAnchor="middle"
            fontSize={11}
            fill="#B0B0C0">
            {cat}
          </text>
        ))}

        {/* Tooltip  start*/}
        {tooltip && (
          <g style={{ pointerEvents: "none" }}>
            <rect
              x={tooltip.x}
              y={tooltip.y}
              width={TOOLTIP_W}
              height={TOOLTIP_H}
              rx={8}
              fill="white"
              filter="drop-shadow(0 2px 10px rgba(0,0,0,0.13))"
            />
            <text
              x={tooltip.x + TOOLTIP_W / 2}
              y={tooltip.y + 18}
              textAnchor="middle"
              fontSize={11}
              fill="#8A8A9A">
              {[tooltip.seriesName, tooltip.catLabel]
                .filter(Boolean)
                .join(" · ")}
            </text>
            <text
              x={tooltip.x + TOOLTIP_W / 2}
              y={tooltip.y + 38}
              textAnchor="middle"
              fontSize={15}
              fontWeight="700"
              fill="#181C32">
              {tooltip.displayValue}
            </text>
          </g>
        )}
      </svg>
    </div>
  );
}
